"use client"

import React, { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import {
  type User,
  clearSession,
  getMe,
  getSession,
  logout as apiLogout,
  setSession,
} from "@/lib/api/auth"
import { currentDestination, loginHref } from "@/lib/access/safe-redirect"
import { type GlobalRole } from "@/lib/api/iam"
import { identifyUser, resetAnalytics } from "@/lib/analytics/posthog"

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserContextValue {
  user: User | null
  /** True while the initial getMe() call is in-flight */
  isLoading: boolean
  /** Re-fetch the user from the backend (e.g. after a profile update) */
  refetch: () => Promise<void>
  /**
   * End the session at WorkOS and locally, then land on /login. Async because
   * it now waits for the revocation it used to skip; the result is not worth
   * awaiting at a call site — it always signs out, revoked or not.
   */
  logout: () => Promise<void>
  /** Returns true if the user holds any of the given GlobalRoles */
  hasRole: (...roles: GlobalRole[]) => boolean
  /**
   * True from the moment logout() is called until the next page takes over.
   * The route guards watch `user` going null and bounce to login carrying the
   * destination; on a deliberate sign-out that bounce is both wrong and a race
   * this flag settles. Read it in a guard, not in a page.
   */
  isSigningOut: boolean
}

// ─── Context ──────────────────────────────────────────────────────────────────

const UserContext = createContext<UserContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function UserProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()

  // Seed from localStorage immediately so the rest of the app never sees a
  // null flash on first render for already-authenticated users.
  const [user, setUser] = useState<User | null>(() => getSession())
  const [isLoading, setIsLoading] = useState(true)
  const [isSigningOut, setIsSigningOut] = useState(false)

  const logout = useCallback(async () => {
    // Raise this BEFORE clearing the user. Dropping `user` to null is exactly
    // what the route guards treat as "your session died under you", and they
    // answer it by bouncing to /login?next=<the page you were on>. That bounce
    // runs in an effect, so it lands AFTER the replace below and wins — which
    // is why sign-out used to leave `next` hanging off the URL. The flag tells
    // the guards this exit is deliberate and to stand down. It also puts the
    // UI in its signing-out state for the round trip below.
    setIsSigningOut(true)
    try {
      // The call that actually ends the session. Until this shipped, signing
      // out only emptied localStorage: the httpOnly cookie pair stayed in the
      // browser and the WorkOS session was never revoked, so the refresh token
      // kept minting access tokens for the cookie's full 7 days. Script cannot
      // delete an httpOnly cookie or revoke anything — only the endpoint can.
      await apiLogout()
    } catch (err) {
      // Caught deliberately, with a concrete strategy: complete the local
      // sign-out anyway. Never strand someone in a session they asked to
      // leave. Logged rather than swallowed, because a silent failure here
      // means revocation has quietly stopped happening — the exact state this
      // change exists to end.
      console.error("logout: the session may not have been revoked", err)
    } finally {
      clearSession()
      resetAnalytics()
      setUser(null)
      // Plain /login. Signing out is a decision to leave; carrying the page
      // they were on would pull them straight back into it at the next
      // sign-in, on what may not be their own machine.
      router.replace("/login")
    }
  }, [router])

  useEffect(() => {
    if (!user) return
    identifyUser(user.id, {
      email: user.email,
      tenant_id: user.organization_id,
      tenant_name: user.companyName,
      role: user.role,
    })
  }, [user])

  const fetchUser = useCallback(async () => {
    setIsLoading(true)
    try {
      const fresh = await getMe()
      if (!fresh) {
        // Backend returned a non-ok response - token expired or revoked.
        // Middleware could not catch this: the cookie is still in the jar, so
        // the request was waved through and only getMe() knows the session is
        // over. Carry the destination or the deep link that got us here is
        // lost at the last step.
        clearSession()
        setUser(null)
        router.replace(loginHref(currentDestination()))
        return
      }
      setSession(fresh)
      setUser(fresh)
      // A session again: whatever sign-out raised this is long over.
      setIsSigningOut(false)
    } catch {
      // Network error or unexpected failure - treat as unauthenticated
      clearSession()
      setUser(null)
      router.replace(loginHref(currentDestination()))
    } finally {
      setIsLoading(false)
    }
  }, [router])

  useEffect(() => {
    // Only fetch if there is an existing session - avoids a getMe() call on
    // the login page (no cookie) which would create a redirect loop.
    if (!getSession()) {
      setIsLoading(false)
      return
    }
    fetchUser()
  }, [fetchUser])

  const hasRole = useCallback(
    (...roles: GlobalRole[]) => {
      if (!user) return false
      return roles.some((r) => user.global_roles.includes(r))
    },
    [user],
  )

  return (
    <UserContext.Provider value={{ user, isLoading, refetch: fetchUser, logout, hasRole, isSigningOut }}>
      {children}
    </UserContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error("useUser() must be called inside <UserProvider>")
  return ctx
}
